import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";

export type TagVariant =
	| "primary"
	| "brand"
	| "neutral"
	| "success"
	| "warning"
	| "error"
	| "info"
	| "outline"
	| "orange";

export type TagSize = "large" | "small";

// Design-system Tag (Figma "Tags"): variant (color) x size x optional leading icon.
// Large: 14px / 32px tall / 8px 16px padding. Small: 12px / 4px 8px padding.
const useStyles = makeStyles()((theme) => ({
	tag: {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		borderRadius: 16,
		fontFamily: theme.typography.fontFamily,
		fontWeight: 400,
		whiteSpace: "nowrap",
		width: "fit-content",
	},
	large: {
		height: 32,
		padding: "8px 16px",
		fontSize: 14,
		lineHeight: "20px",
	},
	small: {
		padding: "4px 8px",
		fontSize: 12,
		lineHeight: "16px",
	},
	primary: {
		backgroundColor: theme.palette.custom.tagPrimarySurface,
		color: theme.palette.custom.tagPrimaryText,
	},
	brand: {
		backgroundColor: theme.palette.custom.tagBrandSurface,
		color: theme.palette.custom.tagBrandText,
	},
	neutral: {
		backgroundColor: theme.palette.custom.tagNeutralSurface,
		color: theme.palette.custom.tagNeutralText,
	},
	success: {
		backgroundColor: theme.palette.custom.tagSuccessSurface,
		color: theme.palette.custom.tagSuccessText,
	},
	warning: {
		backgroundColor: theme.palette.custom.tagWarningSurface,
		color: theme.palette.custom.tagWarningText,
	},
	error: {
		backgroundColor: theme.palette.custom.tagErrorSurface,
		color: theme.palette.custom.tagErrorText,
	},
	info: {
		backgroundColor: theme.palette.custom.tagInfoSurface,
		color: theme.palette.custom.tagInfoText,
	},
	outline: {
		backgroundColor: theme.palette.custom.tagOutlineSurface,
		color: theme.palette.custom.tagOutlineText,
		border: `1px solid ${theme.palette.custom.tagOutlineBorder}`,
	},
	orange: {
		backgroundColor: theme.palette.custom.tagOrangeSurface,
		color: theme.palette.custom.tagOrangeText,
	},
	icon: {
		display: "inline-flex",
		alignItems: "center",
	},
}));

interface Props {
	label: string;
	variant: TagVariant;
	size?: TagSize;
	icon?: React.ReactNode;
	className?: string;
}

export const Tag: React.FC<Props> = ({ label, variant, size = "large", icon, className }) => {
	const { classes } = useStyles();
	return (
		<span
			className={`${classes.tag} ${classes[size]} ${classes[variant]} ${className ?? ""}`}
		>
			{icon && <span className={classes.icon}>{icon}</span>}
			{label}
		</span>
	);
};
